import { i18n } from '../../../../translate/i18n'
import { FC, useState } from 'react'
import { HotelOrdersTabProps, HotelOrder, OrderProduct } from '../types/tabTypes'

const HotelOrdersTab: FC<HotelOrdersTabProps> = ({ orders, totalSpent, totalPay, loading, showList }) => {
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set())

  const toggleOrder = (orderId: number) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev)
      if (newSet.has(orderId)) {
        newSet.delete(orderId)
      } else {
        newSet.add(orderId)
      }
      return newSet
    })
  }
  const formatCurrency = (value: number) => {
    return (value / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR')
  }

  const getOrderTypeIcon = (type: string) => {
    switch (type) {
      case 'delivery':
        return 'fas fa-truck'
      case 'withdraw':
        return 'fas fa-shopping-bag'
      default:
        return 'fas fa-shopping-cart'
    }
  }

  const getOrderTypeLabel = (type: string) => {
    switch (type) {
      case 'delivery':
        return i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_delivery')
      case 'withdraw':
      default:
        return i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_order')
    }
  }

  const getStatusBadgeClass = (slug: string) => {
    switch (slug) {
      case 'waiting':
        return 'badge-warning'
      case 'open':
        return 'badge-info'
      case 'completed':
        return 'badge-success'
      case 'cancelled':
        return 'badge-danger'
      default:
        return 'badge-secondary'
    }
  }

  const getPaymentTypeLabel = (order: HotelOrder) => {
    const gateway = order.content?.dataGateway
    if (!gateway) return i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_payment_na')

    if (gateway.typeTicket === 'payOffline') {
      return i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_payment_offline')
    } else if (gateway.typeTicket === 'cielo') {
      return `${i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_payment_card')} - ${gateway.flagIdentifier || i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_payment_credit')}`
    }

    return gateway.gatewayName || i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_payment_na')
  }

  const calculateOrderTotal = (order: HotelOrder) => {
    const productsTotal = order.products.reduce((sum, product) => sum + (product.price * product.quantity), 0)
    const deliveryFee = order.content?.deliveryFee || 0
    return productsTotal + deliveryFee
  }

  if (loading) {
    return (
      <div className='text-center py-10'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>{i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_loading')}</span>
        </div>
        <h5 className='mt-3'>{i18n.t('hospitality_room_reservation.label_loading_orders')}</h5>
      </div>
    )
  }

  if (orders.length === 0) {
    return (
      <div className='text-center py-10'>
        <i className='fas fa-shopping-cart text-muted fs-3x mb-4'></i>
        <h3 className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_no_data')}</h3>
        <p className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_pending')}</p>
      </div>
    )
  }

  return (
    <div className='card card-xxl-stretch'>
      <div className='card-header py-5'>
        <h3 className='card-title'>
          <i className='fas fa-shopping-cart me-2'></i>
          {i18n.t('hospitality_room_reservation_tabs.hotel_orders.tab_title')}
        </h3>
        <div className='card-toolbar'>
          <span className='badge badge-light-primary me-2'>
            {orders.length} {orders.length === 1 ? i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_order') : i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_orders')}
          </span>
          <span className='badge badge-light-success'>
            {i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_total_spent').replace('{value}', formatCurrency(totalSpent))}
          </span>
        </div>
      </div>

      {/* Resumo Financeiro dos Pedidos */}
      <div className='card-body'>
        <div className='row mb-6'>
          <div className='col-md-4'>
            <div className='text-center p-4 bg-light-primary rounded'>
              <i className='fas fa-receipt text-primary fs-2x mb-2'></i>
              <h3 className='text-primary'>{orders.length}</h3>
              <p className='text-muted mb-0'>{i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_total_orders')}</p>
            </div>
          </div>
          <div className='col-md-4'>
            <div className='text-center p-4 bg-light-success rounded'>
              <i className='fas fa-money-bill-wave text-success fs-2x mb-2'></i>
              <h3 className='text-success'>{formatCurrency(totalSpent)}</h3>
              <p className='text-muted mb-0'>{i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_total_spent_label')}</p>
            </div>
          </div>
          <div className='col-md-4'>
            <div className='text-center p-4 bg-light-info rounded'>
              <i className='fas fa-credit-card text-info fs-2x mb-2'></i>
              <h3 className='text-info'>{formatCurrency(totalPay)}</h3>
              <p className='text-muted mb-0'>{i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_total_paid_label')}</p>
            </div>
          </div>
        </div>

        {/* Lista de Pedidos */}
        <div className='row'>
          {showList && orders.map((order) => {
            const isExpanded = expandedOrders.has(order.id)
            return (
            <div key={order.id} className='col-12 mb-6'>
              <div className='card card-flush border'>
                <div 
                  className='card-header bg-light' 
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleOrder(order.id)}
                >
                  <div className='d-flex align-items-center justify-content-between w-100'>
                    <div className='d-flex align-items-center'>
                      <i className={`${getOrderTypeIcon(order.type)} text-primary fs-2x me-3`}></i>
                      <div>
                        <h4 className='mb-1'>Pedido #{order.orderidExt}</h4>
                        <div className='d-flex align-items-center'>
                          <span className='text-muted me-3'>
                            <i className='fas fa-user me-1'></i>
                            {order.content?.customer}
                          </span>
                          <span className='text-muted me-3'>
                            <i className='fas fa-calendar me-1'></i>
                            {formatDate(order.created)}
                          </span>
                          <span className={`badge ${getStatusBadgeClass(order.status.slug)} me-3`}>
                            {order.status.name}
                          </span>
                          <span className='badge badge-light-secondary'>
                            {getOrderTypeLabel(order.type)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className='text-end d-flex align-items-center'>
                      <div className='me-3'>
                        <h4 className='text-success mb-1'>{formatCurrency(calculateOrderTotal(order))}</h4>
                        <small className='text-muted'>{getPaymentTypeLabel(order)}</small>
                      </div>
                      <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-muted`}></i>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                <div className='card-body'>
                  <div className='row'>
                    <div className='col-md-8'>
                      <h6 className='mb-3'>
                        <i className='fas fa-box me-2'></i>
                        {i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_products')
                        .replace('{count}',order.products.length.toString())}
                      </h6>
                      <div className='row'>
                        {order.products.map((product: OrderProduct) => (
                          <div key={product.id} className='col-md-6 mb-3'>
                            <div className='d-flex align-items-center p-3 bg-light rounded'>
                              {product.content.image && (
                                <img
                                  src={product.content.image}
                                  alt={product.content.productName}
                                  className='rounded me-3'
                                  style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                />
                              )}
                              <div className='flex-grow-1'>
                                <h6 className='mb-1'>{product.content.productName}</h6>
                                <div className='d-flex justify-content-between'>
                                  <span className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_quantity')} {product.quantity}x</span>
                                  <span className='fw-bold text-success'>
                                    {formatCurrency(product.price * product.quantity)}
                                  </span>
                                </div>
                                <small className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_sku')} {product.sku}</small>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className='col-md-4'>
                      <h6 className='mb-3'>
                        <i className='fas fa-info-circle me-2'></i>
                        {i18n.t('hospitality_room_reservation.label_order_details')}
                      </h6>

                      {/* Endereço de Entrega/Retirada */}
                      <div className='mb-4 p-3 bg-light rounded'>
                        <h6 className='mb-2'>
                          <h3 className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.hotel_orders.no_order_title')}</h3>
                          <i className={`${order.type === 'delivery' ? 'fas fa-map-marker-alt' : 'fas fa-store'} me-2`}></i>
                          {order.type === 'delivery' ? i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_delivery_address') : i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_withdraw_address')}
                        </h6>
                        <address className='mb-0'>
                          <strong>{order.content.orderAddress.name}</strong><br />
                          {order.content.orderAddress.address}, {order.content.orderAddress.number}<br />
                          {order.content.orderAddress.complement && (
                            <>{order.content.orderAddress.complement}<br /></>
                          )}
                          {order.content.orderAddress.district} - {order.content.orderAddress.city}/{order.content.orderAddress.state}<br />
                          CEP: {order.content.orderAddress.cep}
                        </address>
                      </div>

                      {/* Resumo Financeiro */}
                      <div className='p-3 bg-light-success rounded'>
                        <h6 className='text-success mb-2'>
                          <i className='fas fa-calculator me-2'></i>
                          {i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_financial_summary')}
                        </h6>
                        <div className='d-flex justify-content-between mb-1'>
                          <span>{i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_products_label')}</span>
                          <span>{formatCurrency(order.products.reduce((sum, p) => sum + (p.price * p.quantity), 0))}</span>
                        </div>
                        {order.content.deliveryFee > 0 && (
                          <div className='d-flex justify-content-between mb-1'>
                            <span>{i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_delivery_fee')}</span>
                            <span>{formatCurrency(order.content.deliveryFee)}</span>
                          </div>
                        )}
                        {parseFloat(order.discountGeneral) > 0 && (
                          <div className='d-flex justify-content-between mb-1 text-warning'>
                            <span>{i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_discount')}</span>
                            <span>-{formatCurrency(parseFloat(order.discountGeneral) * 100)}</span>
                          </div>
                        )}
                        <hr className='my-2' />
                        <div className='d-flex justify-content-between fw-bold text-success'>
                          <span>{i18n.t('hospitality_room_reservation_tabs.hotel_orders.label_total')}</span>
                          <span>{formatCurrency(calculateOrderTotal(order))}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>
          )})}
        </div>
      </div>
    </div>
  )
}

export default HotelOrdersTab